import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NetFaturamento {
  id: string;
  boletim_id: string;
  contrato_id: string;
  valor: number;
  numero_nf: string | null;
  status: 'emitido' | 'pago' | 'parcial' | 'inadimplente' | 'cancelado';
  data_emissao: string;
  data_vencimento: string | null;
  data_pagamento: string | null;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface NetPagamentoEvidencia {
  id: string;
  faturamento_id: string;
  tipo: 'upload_comprovante' | 'declaracao_manual';
  arquivo_url: string | null;
  hash_documento: string | null;
  valor_comprovado: number | null;
  observacoes: string | null;
  created_at: string;
  created_by: string | null;
}

export function useNetFaturamentos(contratoId?: string) {
  return useQuery({
    queryKey: ['net-faturamentos', contratoId],
    queryFn: async () => {
      let query = supabase
        .from('net_faturamentos')
        .select('*')
        .order('data_emissao', { ascending: false });

      if (contratoId) {
        query = query.eq('contrato_id', contratoId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as NetFaturamento[];
    },
    enabled: !!contratoId,
  });
}

export function useNetPagamentosEvidencias(faturamentoId?: string) {
  return useQuery({
    queryKey: ['net-pagamentos-evidencias', faturamentoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('net_pagamentos_evidencias')
        .select('*')
        .eq('faturamento_id', faturamentoId!)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as NetPagamentoEvidencia[];
    },
    enabled: !!faturamentoId,
  });
}

export function useCreateFaturamento() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      boletim_id: string;
      contrato_id: string;
      valor: number;
      numero_nf?: string;
      data_vencimento?: string;
      observacoes?: string;
    }) => {
      const { data, error } = await supabase
        .from('net_faturamentos')
        .insert({
          boletim_id: params.boletim_id,
          contrato_id: params.contrato_id,
          valor: params.valor,
          numero_nf: params.numero_nf || null,
          data_vencimento: params.data_vencimento || null,
          observacoes: params.observacoes || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['net-faturamentos', vars.contrato_id] });
      queryClient.invalidateQueries({ queryKey: ['net-governance-ativa'] });
    },
  });
}

export function useUpdateFaturamentoStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      status: NetFaturamento['status'];
      data_pagamento?: string;
    }) => {
      const updateData: Record<string, unknown> = { status: params.status };
      if (params.data_pagamento) updateData.data_pagamento = params.data_pagamento;

      const { data, error } = await supabase
        .from('net_faturamentos')
        .update(updateData)
        .eq('id', params.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['net-faturamentos'] });
      queryClient.invalidateQueries({ queryKey: ['net-governance-ativa'] });
    },
  });
}

export function useCreatePagamentoEvidencia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      faturamento_id: string;
      tipo: 'upload_comprovante' | 'declaracao_manual';
      arquivo_url?: string;
      hash_documento?: string;
      valor_comprovado?: number;
      observacoes?: string;
    }) => {
      const { data, error } = await supabase
        .from('net_pagamentos_evidencias')
        .insert({
          faturamento_id: params.faturamento_id,
          tipo: params.tipo,
          arquivo_url: params.arquivo_url || null,
          hash_documento: params.hash_documento || null,
          valor_comprovado: params.valor_comprovado || null,
          observacoes: params.observacoes || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['net-pagamentos-evidencias', vars.faturamento_id] });
    },
  });
}
